"use client";

import * as React from "react";
import { useTranslations } from "next-intl";
import { Input } from "@/components/ui/input";
import { EyeIcon, EyeOffIcon, RefreshIcon, LockerIcon } from "@/components/icons";
import { generatePassword } from "@/src/lib/generate-password";

type Props = {
  value: any;
  onChange: (next: any) => void;
};

const PasswordGeneratorField: React.FC<Props> = ({ value, onChange }) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = React.useState(false);
  const [generated, setGenerated] = React.useState(value.password ?? "");
  // Password lock state: when locked (true), password won't be sent to server
  const [isPasswordLocked, setIsPasswordLocked] = React.useState(!!value.id);
  // Track whether the user has manually edited the password field
  const userEditedRef = React.useRef(false);

  React.useEffect(() => {
    // Only auto-generate if password was never set (initial mount), not when user clears it
    if (!value.password && !generated && !userEditedRef.current) {
      const pwd = generatePassword();
      setGenerated(pwd);
      onChange({ ...value, password: pwd });
      return;
    }

    if (value.password && value.password !== generated) {
      setGenerated(value.password);
    }
  }, [value.password]);

  // Reset lock state and user-edited flag when user changes (e.g., opening a different user)
  React.useEffect(() => {
    setIsPasswordLocked(!!value.id);
    userEditedRef.current = false;
  }, [value.id]);

  const setPassword = (pwd: string) => {
    userEditedRef.current = true;
    setGenerated(pwd);
    onChange({
      ...value,
      password: pwd,
      // Mark password as unlocked (will be sent to server)
      _passwordLocked: false,
    });
  };

  const handlePasswordButtonClick = () => {
    if (isPasswordLocked) {
      // When locked: just unlock (allow editing)
      setIsPasswordLocked(false);
      onChange({
        ...value,
        _passwordLocked: false,
      });
    } else {
      // When unlocked: generate new password
      const next = generatePassword();
      setPassword(next);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-blue-600">{t("label-password")}</label>
        <span className="text-xs text-muted-foreground">{t("ui-password-min-8")}</span>
      </div>
      <div className="flex items-center gap-2">
        {/* Button that toggles between locked/unlocked state */}
        <button
          type="button"
          className={`flex items-center justify-center h-10 w-10 shrink-0 rounded-lg border shadow-sm ${
            isPasswordLocked
              ? "border-input bg-white hover:bg-gray-50"
              : "border-amber-300 bg-amber-100 hover:bg-amber-200"
          }`}
          onClick={handlePasswordButtonClick}
          title={isPasswordLocked
            ? (t("ui-unlock-password"))
            : (t("ui-generate"))}
        >
          {isPasswordLocked ? (
            <LockerIcon size={16} />
          ) : (
            <RefreshIcon size={16} className="text-amber-700" />
          )}
        </button>

        <div className={`flex-1 flex items-center rounded-lg border px-3 py-2 ${
          isPasswordLocked
            ? "border-gray-200 bg-gray-100 opacity-60"
            : "border-amber-200 bg-amber-50"
        }`}>
          <Input
            className="bg-transparent border-none shadow-none p-0 h-auto focus-visible:ring-0 flex-1 rounded-none"
            type={showPassword ? "text" : "password"}
            value={generated}
            onChange={(e) => {
              if (!isPasswordLocked) {
                setPassword(e.target.value);
              }
            }}
            disabled={isPasswordLocked}
            placeholder={isPasswordLocked ? "••••••••" : ""}
          />

          <button
            type="button"
            className="shrink-0 text-muted-foreground hover:text-foreground disabled:opacity-50"
            onClick={() => setShowPassword((prev) => !prev)}
            title={showPassword ? t("ui-hide") : t("ui-show")}
            disabled={isPasswordLocked}
          >
            {showPassword ? (
              <EyeOffIcon size={16} />
            ) : (
              <EyeIcon size={16} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordGeneratorField;
